/** The order spine view's panels (plan §2.4): Placed and Bookings on the left, Contact
 *  and Money on the right — or one column in lifecycle order when the width left beside
 *  the docked ledger does not fit two panels. The booking spine's layout rules
 *  (`spineColumns`): the layout follows the edit-mode width in both modes and each
 *  panel's grids are pinned to it, so entering edit mode re-lays nothing out.
 *
 *  `useOrderSpineMarks` is the spine nodes' edit-mode marks: unsaved changes per stage
 *  and how many of them the restech will undo. */
import { Box } from '@mui/material';
import { useMemo, type ComponentType } from 'react';

import type { OrderRecordStage } from '@/api/orderRecord';
import type { StageMarks } from '@/features/bookings/record/views/SpineNav';
import { useDraft } from '@/features/records/draft/DraftProvider';
import { useRecordEditor } from '@/features/records/editor/RecordEditorProvider';
import { FIELD_GRID_VAR, fieldGridColumns } from '@/features/records/panels/fieldGrid';
import { PANEL_GAP_PX, spineColumns } from '@/features/records/views/spineColumns';
import type { ViewSection } from '@/features/records/views/types';

import { BookingsPanel } from '../panels/BookingsPanel';
import { ContactPanel } from '../panels/ContactPanel';
import type { OrderDisplayContext } from '../panels/displays';
import { MoneyPanel } from '../panels/MoneyPanel';
import { IDENTITY_GRID_VAR, identityGridColumns } from '../panels/OrderStagePanel';
import { PlacedPanel } from '../panels/PlacedPanel';
import { ORDER_RECORD_REGISTRY } from '../registry';

import { orderSpineSections, type OrderRecordData } from './spineLayout';

type PanelComponent = ComponentType<{ section: ViewSection<OrderRecordStage>; display: OrderDisplayContext }>;

const PANELS: Record<OrderRecordStage, PanelComponent> = {
  placed: PlacedPanel,
  contact: ContactPanel,
  bookings: BookingsPanel,
  money: MoneyPanel,
};

const LEFT: OrderRecordStage[] = ['placed', 'bookings'];
const RIGHT: OrderRecordStage[] = ['contact', 'money'];
const ONE_COLUMN: OrderRecordStage[] = ['placed', 'contact', 'bookings', 'money'];

/** @param width the page content's width (ledger column included); 0 = unmeasured. */
export function OrderSpineView({ data, width }: { data: OrderRecordData; width: number }) {
  const { editWidth, columns } = spineColumns(width);
  const twoColumns = columns === 2;
  const pin = (panelWidth: number): Record<string, string> =>
    width > 0
      ? { [FIELD_GRID_VAR]: fieldGridColumns(panelWidth), [IDENTITY_GRID_VAR]: identityGridColumns(panelWidth) }
      : {};
  const { catalog, editContext, detail } = data;
  const sections = useMemo(
    () => orderSpineSections(ORDER_RECORD_REGISTRY, { catalog, editContext, detail }),
    [catalog, editContext, detail],
  );
  const display = useMemo<OrderDisplayContext>(
    () => ({ detail, editContext, links: { tenantId: detail.tenant?.id ?? editContext.tenant.id } }),
    [detail, editContext],
  );
  const byStage = new Map(sections.map((s) => [s.id, s]));
  const panel = (stage: OrderRecordStage) => {
    const section = byStage.get(stage);
    if (!section) return null;
    const Panel = PANELS[stage];
    return <Panel key={stage} section={section} display={display} />;
  };
  const column = (stages: OrderRecordStage[], vars: Record<string, string>) => (
    <Box sx={{ display: 'grid', gap: `${PANEL_GAP_PX}px`, minWidth: 0, alignContent: 'start', ...vars }}>
      {stages.map(panel)}
    </Box>
  );

  return (
    <Box
      data-spine-view
      data-columns={twoColumns ? 2 : 1}
      sx={{
        display: 'grid',
        gap: `${PANEL_GAP_PX}px`,
        gridTemplateColumns: twoColumns ? 'minmax(0, 1fr) minmax(0, 1fr)' : 'minmax(0, 1fr)',
        alignItems: 'start',
      }}
    >
      {twoColumns ? (
        <>
          {column(LEFT, pin((editWidth - PANEL_GAP_PX) / 2))}
          {column(RIGHT, pin((editWidth - PANEL_GAP_PX) / 2))}
        </>
      ) : (
        column(ONE_COLUMN, pin(editWidth))
      )}
    </Box>
  );
}

/** Edit-mode marks per spine stage. */
export function useOrderSpineMarks(): Partial<Record<OrderRecordStage, StageMarks>> {
  const { state } = useDraft();
  const { fieldForPath, config } = useRecordEditor();
  return useMemo(() => {
    const out: Partial<Record<OrderRecordStage, StageMarks>> = {};
    for (const [rec, paths] of Object.entries(state.entries)) {
      for (const path of Object.keys(paths)) {
        const field = fieldForPath(path, rec);
        const stage = field ? ORDER_RECORD_REGISTRY[field.key as keyof typeof ORDER_RECORD_REGISTRY]?.stage : undefined;
        if (!field || !stage) continue;
        const m = (out[stage] ??= { count: 0, reverts: 0 });
        m.count += 1;
        if (config.verdictOf(rec, field, state)?.verdict === 'overwrites') m.reverts += 1;
      }
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.version, fieldForPath, config]);
}
